import React, { useEffect, useRef } from "react";

function pointOnEllipse(cx, cy, rx, ry, rotation, angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const cr = Math.cos(rotation);
  const sr = Math.sin(rotation);
  return {
    x: cx + rx * cos * cr - ry * sin * sr,
    y: cy + rx * cos * sr + ry * sin * cr,
  };
}

function buildPaths(width, height) {
  const specs = [
    { cx: 0.54, cy: 0.5, rx: 0.46, ry: 0.44, start: -1.05, end: 1.05, rot: 0.12 },
    { cx: 0.26, cy: 0.5, rx: 0.3, ry: 0.42, start: 0.65, end: 2.45, rot: -0.18 },
    { cx: 0.76, cy: 0.44, rx: 0.26, ry: 0.36, start: 2.0, end: 3.85, rot: 0.28 },
    { cx: 0.5, cy: 0.3, rx: 0.5, ry: 0.09, start: 0.2, end: 2.9, rot: 0 },
    { cx: 0.5, cy: 0.7, rx: 0.48, ry: 0.08, start: -2.75, end: -0.35, rot: 0.04 },
    { cx: 0.16, cy: 0.4, rx: 0.2, ry: 0.32, start: -1.15, end: 1.05, rot: -0.42 },
    { cx: 0.84, cy: 0.58, rx: 0.18, ry: 0.28, start: 1.75, end: 3.35, rot: 0.48 },
  ];

  return specs.map((s, i) => ({
    cx: s.cx * width,
    cy: s.cy * height,
    rx: s.rx * width,
    ry: s.ry * height,
    start: s.start,
    end: s.end,
    rot: s.rot,
    electrons: [
      { progress: (i * 0.31) % 1, speed: 0.006 + (i % 4) * 0.0024 },
      { progress: (i * 0.17 + 0.22) % 1, speed: 0.0055 + (i % 3) * 0.002 },
      { progress: (i * 0.49 + 0.08) % 1, speed: 0.0058 + (i % 5) * 0.0016 },
      { progress: (i * 0.73 + 0.61) % 1, speed: 0.0062 + (i % 3) * 0.0022 },
      ...(i % 2 === 0
        ? [
            { progress: (i * 0.67 + 0.4) % 1, speed: 0.005 + (i % 3) * 0.002 },
            { progress: (i * 0.83 + 0.55) % 1, speed: 0.0065 + (i % 4) * 0.0018 },
            { progress: (i * 0.11 + 0.33) % 1, speed: 0.0052 + (i % 4) * 0.002 },
            { progress: (i * 0.91 + 0.14) % 1, speed: 0.007 + (i % 3) * 0.0015 },
          ]
        : []),
    ],
  }));
}

export default function HeroParticles() {
  const canvasRef = useRef(null);
  const pathsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      w = canvas.width = parent.offsetWidth;
      h = canvas.height = parent.offsetHeight;
      pathsRef.current = buildPaths(w, h);
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      pathsRef.current.forEach((path) => {
        ctx.beginPath();
        ctx.ellipse(path.cx, path.cy, path.rx, path.ry, path.rot, path.start, path.end);
        ctx.strokeStyle = "rgba(18, 28, 24, 0.11)";
        ctx.lineWidth = 0.38;
        ctx.stroke();

        path.electrons.forEach((el) => {
          el.progress = (el.progress + el.speed) % 1;
          const angle = path.start + el.progress * (path.end - path.start);
          const { x, y } = pointOnEllipse(
            path.cx,
            path.cy,
            path.rx,
            path.ry,
            path.rot,
            angle
          );

          ctx.beginPath();
          ctx.arc(x, y, 5.2, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255, 236, 170, 0.22)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x, y, 3.1, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255, 228, 150, 0.88)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x, y, 1.4, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255, 252, 235, 1)";
          ctx.fill();
        });
      });

      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero-particles-canvas" aria-hidden="true" />;
}
